import type { HTMLAttributes, MouseEventHandler, ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

export type CommonTypes = {
    variant?:
        | 'primary'
        | 'secondary'
        | 'tertiary'
        | 'outlined'
        | 'plain'
        | 'transparent'
        | 'danger'
        | 'info'
        | 'success'
        | 'warning'
        | string;
    small?: boolean;
    children?: ReactNode;
    iconStart?: ReactNode;
    iconEnd?: ReactNode;
    dataTestid?: string;
    className?: string;
};

export type ButtonTypes = CommonTypes & {
    type?: 'button';
    onClick: MouseEventHandler;
    disabled?: boolean;
    htmlAttributes?: HTMLAttributes<HTMLButtonElement>;
};

export type ButtonSubmitTypes = CommonTypes & {
    type?: 'submit';
    onClick?: MouseEventHandler;
    disabled?: boolean;
    htmlAttributes?: HTMLAttributes<HTMLButtonElement | HTMLFieldSetElement>;
};

export type LinkTypes = CommonTypes & {
    type?: 'link';
    to: string;
    htmlAttributes?: HTMLAttributes<HTMLAnchorElement>;
    disabled?: boolean;
};

export type ExternalLinkTypes = CommonTypes & {
    type?: 'external-link';
    href: string;
    htmlAttributes?: HTMLAttributes<HTMLAnchorElement>;
};

export type ButtonProps = ButtonTypes | ButtonSubmitTypes | LinkTypes | ExternalLinkTypes;

export const Button = (props: ButtonProps) => {
    // Note : I did used props destructuration to work around a type error on `onClick`, `to` and `href` props, not defined depending on `type`

    const variant = props.variant || 'primary';
    const type = props.type || 'button';
    const small = props.small || false;

    const classNames = twMerge(
        clsx(
            // Common styles
            'text-sm rounded-md gap-2 flex whitespace-nowrap items-center cursor-pointer font-normal',
            'focus:outline-solid focus:outline-2',
            'disabled:text-gray-400 disabled:cursor-not-allowed',
            small ? 'px-2 py-1.5' : 'px-4 py-2',
            props.className,
            // Variant styles
            {
                'bg-button-background text-button-foreground hover:bg-button-background-hover active:bg-button-background-active focus:outline-focus-primary disabled:bg-button-background-disabled':
                    variant === 'primary',
                'bg-gray-200 hover:bg-gray-300 active:bg-gray-400 focus:outline-focus-primary text-black disabled:bg-gray-100':
                    variant === 'secondary',
                'bg-violet-200 hover:bg-violet-300 active:bg-violet-400 focus:outline-focus-primary text-black disabled:bg-violet-100':
                    variant === 'tertiary',
                'bg-white hover:bg-gray-100 active:bg-gray-200 focus:outline-focus-secondary text-black border border-solid border-gray-300 disabled:bg-gray-100':
                    variant === 'outlined',
                'bg-white hover:bg-gray-100 active:bg-gray-200 focus:outline-focus-secondary text-black':
                    variant === 'plain',
                'bg-transparent hover:bg-gray-100 active:bg-gray-200 focus:outline-focus-secondary text-black':
                    variant === 'transparent',
                'bg-red-500 hover:bg-red-600 active:bg-red-700 focus:outline-focus-primary text-white disabled:bg-red-100 disabled:text-white':
                    variant === 'danger',
                'bg-blue-500 hover:bg-blue-600 active:bg-blue-700 focus:outline-focus-primary text-white disabled:bg-blue-100 disabled:text-white':
                    variant === 'info',
                'bg-emerald-500 hover:bg-emerald-600 active:bg-emerald-700 focus:outline-focus-primary text-white disabled:bg-emerald-100 disabled:text-white':
                    variant === 'success',
                'bg-orange-500 hover:bg-orange-600 active:bg-orange-700 focus:outline-focus-primary text-white disabled:bg-orange-100 disabled:text-white':
                    variant === 'warning',
            }
        )
    );

    if (!type || type === 'button') {
        const { onClick, disabled, htmlAttributes } = props as ButtonTypes;
        return (
            <button
                type='button'
                disabled={disabled}
                aria-disabled={disabled}
                onClick={onClick}
                data-testid={props.dataTestid}
                className={classNames}
                {...htmlAttributes}
            >
                {props.iconStart}
                {props.children}
                {props.iconEnd}
            </button>
        );
    } else if (type === 'submit') {
        const { htmlAttributes, disabled, onClick } = props as ButtonSubmitTypes;
        return (
            <button
                type='submit'
                disabled={disabled}
                aria-disabled={disabled}
                data-testid={props.dataTestid}
                className={classNames}
                onClick={onClick}
                {...htmlAttributes}
            >
                {props.iconStart}
                {props.children}
                {props.iconEnd}
            </button>
        );
    } else if (type === 'link') {
        const { to, htmlAttributes, disabled } = props as LinkTypes;

        if (disabled) {
            return (
                <span
                    data-testid={props.dataTestid}
                    className={`${classNames} cursor-not-allowed opacity-50`}
                    {...htmlAttributes}
                >
                    {props.iconStart}
                    {props.children}
                    {props.iconEnd}
                </span>
            );
        }

        return (
            <Link
                to={to}
                data-testid={props.dataTestid}
                className={classNames}
                {...htmlAttributes}
            >
                {props.iconStart}
                {props.children}
                {props.iconEnd}
            </Link>
        );
    }
    if (type === 'external-link') {
        const { href, htmlAttributes } = props as ExternalLinkTypes;
        return (
            <a
                href={href}
                data-testid={props.dataTestid}
                className={classNames}
                target='_blank'
                rel='noopener noreferrer'
                {...htmlAttributes}
            >
                {props.iconStart}
                {props.children}
                {props.iconEnd}
            </a>
        );
    }
    return null;
};
